import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ClinicInfo } from '@/types/clinic';
import { BookingApi } from '@/utils/api';
import { useClinicTheme } from '@/hooks/useClinicTheme';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Footer } from '@/components/Footer';

export function AppointmentConfirmationPage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState<ClinicInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [patientData, setPatientData] = useState<{
    firstName: string;
    lastName: string;
    fullName: string;
    gender: string;
    dateOfBirth: string;
    zipCode: string;
  } | null>(null);
  const [appointmentData, setAppointmentData] = useState<{
    appointmentId: string;
    confirmationNumber: string;
    service: string;
    provider: string;
    date: string;
    time: string;
    amountPaid: string;
  } | null>(null);

  // Cookie helper functions
  const getCookie = (name: string) => {
    const match = document.cookie.match(
      new RegExp(
        '(?:^|; )' +
          name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') +
          '=([^;]*)'
      )
    );
    return match ? decodeURIComponent(match[1]) : undefined;
  };

  // Load patient and appointment data from cookies
  useEffect(() => {
    const firstName = getCookie('patient_first_name');
    const lastName = getCookie('patient_last_name');
    const fullName = getCookie('patient_full_name');
    const gender = getCookie('patient_gender');
    const dateOfBirth = getCookie('patient_dob');
    const zipCode = getCookie('patient_zip_code');

    if (firstName && lastName) {
      setPatientData({
        firstName,
        lastName,
        fullName: fullName || `${firstName} ${lastName}`,
        gender: gender || '',
        dateOfBirth: dateOfBirth || '',
        zipCode: zipCode || '',
      });
    }

    // Load appointment confirmation data
    const appointmentId = getCookie('appointment_id');
    const confirmationNumber = getCookie('confirmation_number');
    const service = getCookie('appointment_service');
    const provider = getCookie('appointment_provider');
    const date = getCookie('appointment_date_confirmed');
    const time = getCookie('appointment_time_confirmed');
    const amountPaid = getCookie('amount_paid');

    if (appointmentId && confirmationNumber) {
      setAppointmentData({
        appointmentId,
        confirmationNumber,
        service: service || 'Service',
        provider: provider || 'Provider',
        date: date || '',
        time: time || '',
        amountPaid: amountPaid || '0',
      });
    }
  }, []);

  useEffect(() => {
    const fetchClinic = async () => {
      if (!clinicSlug) {
        setError('No clinic slug provided');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const cfg = await BookingApi.getClinicConfigBySlug(clinicSlug);
        const cs = (cfg.active_theme?.theme?.color_scheme ||
          cfg.active_theme?.color_scheme) as unknown as Record<string, string>;
        const clinicData: ClinicInfo = {
          id: String(cfg.location.id),
          name: cfg.location.name,
          slug: cfg.location.slug,
          description: cfg.ai_agent?.greeting_message || '',
          address: '',
          phone: '',
          email: '',
          website: undefined,
          theme: {
            primaryColor: cs?.primary || '#2563eb',
            secondaryColor: cs?.secondary || '#1d4ed8',
            accentColor: cs?.main || cs?.primary || '#2563eb',
            backgroundColor: '#FFFFFF',
            textColor: cs?.['text-color'] || '#1F2937',
            mainColor: cs?.primary || '#2563eb',
            gradient: (cs?.gradient as string) ?? null,
            iconStroke: cs?.['icon-stroke'] || cs?.secondary || undefined,
          },
          services: [],
          doctors: [],
          workingHours: {
            monday: { isOpen: false },
            tuesday: { isOpen: false },
            wednesday: { isOpen: false },
            thursday: { isOpen: false },
            friday: { isOpen: false },
            saturday: { isOpen: false },
            sunday: { isOpen: false },
          },
          isActive: true,
          aiAgent: cfg.ai_agent,
        };

        if (clinicData) {
          setClinic(clinicData);
        } else {
          setError('Clinic not found');
        }
      } catch (err) {
        setError('Error loading clinic information');
        console.error('Error fetching clinic:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchClinic();
  }, [clinicSlug]);

  // Apply clinic theme
  useClinicTheme(
    clinic?.theme || {
      primaryColor: '#20B2AA',
      secondaryColor: '#008B8B',
      accentColor: '#FF6B6B',
      backgroundColor: '#FFFFFF',
      textColor: '#1F2937',
      mainColor: '#20B2AA',
    }
  );

  const handleChatSupport = () => {
    // Navigate to chat page
    navigate(`/${clinicSlug}/chat`);
  };

  if (loading) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>Clinic not found</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className='min-h-screen w-full relative flex flex-col '
      style={{
        overflow: 'auto',
        height: '100vh',
      }}
    >
      {/* Header with Logo */}
      <div className='flex-shrink-0 p-6  '>
        <div className='flex items-center'>
          <div className='w-fit'>
            <img
              src={'/logo.png'}
              alt={`${clinic.name} logo`}
              className='w-full h-full object-contain'
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1 px-6 py-8'>
        <div className='max-w-2xl mx-auto w-full'>
          {/* Confirmation Card */}
          <div className='bg-white rounded-2xl shadow-lg overflow-hidden'>
            {/* Header Banner */}
            <div
              className='px-6 py-4'
              style={{ backgroundColor: 'var(--clinic-primary)' }}
            >
              <h1 className='text-2xl font-bold text-white text-center'>
                Appointment Confirmed!
              </h1>
            </div>

            {/* Doctor Information */}
            <div className='px-6 py-6 border-b border-gray-200'>
              <div className='flex items-center space-x-4'>
                <div className='w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden'>
                  <img
                    src='/avatar.jpg'
                    alt={appointmentData?.provider || 'Provider'}
                    className='w-full h-full object-cover'
                  />
                </div>
                <div>
                  <h2 className='text-base font-semibold text-gray-900'>
                    {appointmentData?.provider || 'Provider'}
                  </h2>
                  <p className='text-gray-500 text-sm font-normal'>
                    Healthcare Provider
                  </p>
                </div>
              </div>
            </div>

            {/* Appointment Details */}
            <div className='px-6 py-6 border-b border-gray-200'>
              <div className='space-y-4'>
                <div className='flex items-center space-x-3'>
                  <div className='w-6 h-6 flex items-center justify-center'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='20'
                      height='21'
                      viewBox='0 0 20 21'
                      fill='none'
                    >
                      <g clipPath='url(#clip0_3733_23917)'>
                        <path
                          d='M19.5832 6.41005C19.5626 5.03461 19.0088 3.74113 18.024 2.76786C17.0393 1.79474 15.7394 1.25612 14.3611 1.25122C12.7756 1.25122 11.568 2.07928 10.5145 2.95073C10.5133 2.95073 10.5121 2.95073 10.5109 2.95073C10.0244 2.95073 9.50982 2.65742 8.91636 2.31916C8.08168 1.84333 7.04297 1.25122 5.63961 1.25122C2.80276 1.25122 0.460124 3.56548 0.417449 6.41008C0.399068 7.63271 0.937569 10.3303 1.72692 12.9702C2.2893 14.851 3.4426 18.2192 4.84753 19.6454C5.34826 20.1537 5.87216 20.4115 6.40448 20.4115C6.79721 20.4115 7.15258 20.2482 7.43218 19.9395C7.62246 19.7293 7.7792 19.4498 7.91127 19.085C8.11713 18.5166 8.24579 17.7937 8.38202 17.0283C8.73155 15.0645 9.07291 13.5615 10.0022 13.556C10.9314 13.5615 11.2728 15.0645 11.6223 17.0283C11.7586 17.7936 11.8872 18.5165 12.0931 19.085C12.2252 19.4498 12.3819 19.7293 12.5722 19.9395C12.8486 20.2447 13.1989 20.4077 13.5863 20.4114C13.5903 20.4114 13.5942 20.4114 13.5983 20.4114C13.5988 20.4114 13.5993 20.4114 13.5999 20.4114C13.6024 20.4114 13.605 20.4114 13.6075 20.4114C14.136 20.4078 14.656 20.1501 15.1532 19.6453C16.5581 18.2192 17.7113 14.8509 18.2737 12.9701C19.0631 10.3303 19.6016 7.63267 19.5832 6.41005ZM16.8396 12.5412C15.9503 15.5151 14.947 17.7216 14.0868 18.5947C13.9009 18.7835 13.7525 18.875 13.6572 18.9039C13.6103 18.8381 13.5189 18.6746 13.4139 18.3025C13.2938 17.8772 13.1978 17.3374 13.096 16.7658C12.924 15.7995 12.729 14.7041 12.3493 13.8379C11.7117 12.3834 10.7355 12.0657 10.0201 12.0591C10.0159 12.059 10.0116 12.059 10.0073 12.059H9.99696C9.99269 12.059 9.98842 12.059 9.98415 12.0591C9.26877 12.0657 8.29262 12.3834 7.65496 13.838C7.27526 14.7041 7.08029 15.7994 6.90828 16.7659C6.80657 17.3374 6.71047 17.8773 6.59046 18.3025C6.48497 18.6763 6.39321 18.8395 6.34646 18.9049C6.2513 18.8769 6.10182 18.7856 5.9139 18.5948C5.05372 17.7216 4.05039 15.5152 3.16112 12.5413C2.33322 9.77256 1.90077 7.32746 1.91421 6.43254C1.94472 4.40092 3.61596 2.74809 5.63969 2.74809C6.64635 2.74809 7.42353 3.19113 8.17508 3.61946C8.48313 3.79507 8.7956 3.97322 9.12199 4.11903C8.38441 4.69886 7.67045 5.11723 6.83637 5.11723V6.61414C8.53378 6.61414 9.75427 5.56802 10.9346 4.55638C12.0196 3.62643 13.0445 2.74802 14.3584 2.74802C16.3837 2.75524 18.0561 4.40807 18.0865 6.43247C18.0999 7.32739 17.6674 9.77248 16.8396 12.5412Z'
                          fill='#535862'
                        />
                      </g>
                      <defs>
                        <clipPath id='clip0_3733_23917'>
                          <rect
                            width='20'
                            height='20'
                            fill='white'
                            transform='translate(0 0.831299)'
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                  <span className='text-gray-900 font-semibold text-base'>
                    {appointmentData?.service || 'Service'}
                  </span>
                </div>
                <div className='flex items-center space-x-3'>
                  <div className='w-6 h-6 flex items-center justify-center'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='20'
                      height='21'
                      viewBox='0 0 20 21'
                      fill='none'
                    >
                      <path
                        d='M10.0003 5.83126V10.8313L13.3337 12.4979M18.3337 10.8313C18.3337 15.4336 14.6027 19.1646 10.0003 19.1646C5.39795 19.1646 1.66699 15.4336 1.66699 10.8313C1.66699 6.22889 5.39795 2.49792 10.0003 2.49792C14.6027 2.49792 18.3337 6.22889 18.3337 10.8313Z'
                        stroke='var(--clinic-icon-stroke)'
                        strokeWidth='1.66667'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                  <span className='text-gray-900 font-semibold text-base'>
                    {appointmentData?.date && appointmentData?.time
                      ? `${new Date(appointmentData.date).toLocaleDateString(
                          'en-US',
                          {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          }
                        )} at ${new Date(
                          `2000-01-01T${appointmentData.time}`
                        ).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        })}`
                      : 'Date and time'}
                  </span>
                </div>
                {appointmentData?.confirmationNumber && (
                  <div className='flex items-center space-x-3'>
                    <div className='w-6 h-6 flex items-center justify-center'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='21'
                        viewBox='0 0 20 21'
                        fill='none'
                      >
                        <path
                          d='M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
                          stroke='var(--clinic-icon-stroke)'
                          strokeWidth='1.66667'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </div>
                    <span className='text-gray-900 font-semibold text-base'>
                      Confirmation: {appointmentData.confirmationNumber}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Patient Information */}
            {patientData && (
              <div className='px-6 py-6 border-b border-gray-200'>
                <h3 className='text-lg font-semibold text-gray-900 mb-4'>
                  Patient Information
                </h3>
                <div className='space-y-3'>
                  <div className='flex items-center space-x-3'>
                    <div className='w-6 h-6 flex items-center justify-center'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='21'
                        viewBox='0 0 20 21'
                        fill='none'
                      >
                        <path
                          d='M10 10.5C12.7614 10.5 15 8.26142 15 5.5C15 2.73858 12.7614 0.5 10 0.5C7.23858 0.5 5 2.73858 5 5.5C5 8.26142 7.23858 10.5 10 10.5Z'
                          stroke='var(--clinic-icon-stroke)'
                          strokeWidth='1.66667'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M2.5 19.5C2.5 15.3579 5.85786 12 10 12C14.1421 12 17.5 15.3579 17.5 19.5'
                          stroke='#535862'
                          strokeWidth='1.66667'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </div>
                    <span className='text-gray-900 font-semibold text-base'>
                      {patientData.fullName}
                    </span>
                  </div>

                  {patientData.dateOfBirth && (
                    <div className='flex items-center space-x-3'>
                      <div className='w-6 h-6 flex items-center justify-center'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='20'
                          height='21'
                          viewBox='0 0 20 21'
                          fill='none'
                        >
                          <path
                            d='M15.8333 3.5H4.16667C3.24619 3.5 2.5 4.24619 2.5 5.16667V16.8333C2.5 17.7538 3.24619 18.5 4.16667 18.5H15.8333C16.7538 18.5 17.5 17.7538 17.5 16.8333V5.16667C17.5 4.24619 16.7538 3.5 15.8333 3.5Z'
                            stroke='var(--clinic-icon-stroke)'
                            strokeWidth='1.66667'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                          <path
                            d='M13.3333 1.5V5.5M6.66667 1.5V5.5M2.5 8.5H17.5'
                            stroke='var(--clinic-icon-stroke)'
                            strokeWidth='1.66667'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </div>
                      <span className='text-gray-600 text-base'>
                        Date of Birth:{' '}
                        {new Date(patientData.dateOfBirth).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Location Details */}
            <div className='px-6 py-6 border-b border-gray-200'>
              <div className='flex items-start space-x-3'>
                <div className='w-6 h-6 flex items-center justify-center mt-1'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='20'
                    height='21'
                    viewBox='0 0 20 21'
                    fill='none'
                  >
                    <g clipPath='url(#clip0_3733_23928)'>
                      <path
                        d='M17.5 9.16467C17.5 14.998 10 19.998 10 19.998C10 19.998 2.5 14.998 2.5 9.16467C2.5 7.17555 3.29018 5.26789 4.6967 3.86137C6.10322 2.45485 8.01088 1.66467 10 1.66467C11.9891 1.66467 13.8968 2.45485 15.3033 3.86137C16.7098 5.26789 17.5 7.17555 17.5 9.16467Z'
                        stroke='var(--clinic-icon-stroke)'
                        strokeWidth='1.66667'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M10 11.6647C11.3807 11.6647 12.5 10.5454 12.5 9.16467C12.5 7.78396 11.3807 6.66467 10 6.66467C8.61929 6.66467 7.5 7.78396 7.5 9.16467C7.5 10.5454 8.61929 11.6647 10 11.6647Z'
                        stroke='var(--clinic-icon-stroke)'
                        strokeWidth='1.66667'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </g>
                    <defs>
                      <clipPath id='clip0_3733_23928'>
                        <rect
                          width='20'
                          height='20'
                          fill='white'
                          transform='translate(0 0.831299)'
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <div>
                  <h3 className='text-lg font-semibold text-gray-900'>
                    {clinic.name}
                  </h3>
                  <p className='text-gray-900 text-base font-normal'>
                    {clinic.address}
                  </p>
                  <p className='text-gray-900 text-base font-normal'>
                    {clinic.phone}
                  </p>
                </div>
              </div>
            </div>

            {/* Google Maps */}
            <div className='px-6 py-6'>
              <div className='w-full h-48 rounded-lg overflow-hidden'>
                <iframe
                  src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.675968336415!2d-73.98561892358512!3d40.74715503546203!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c259a8281726ad%3A0xd3ad2d4697d3fb80!2s169%20Madison%20Ave%2C%20New%20York%2C%20NY%2010016%2C%20USA!5e0!3m2!1sen!2s!4v1758107699278!5m2!1sen!2s'
                  width='100%'
                  height='100%'
                  style={{ border: 0 }}
                  allowFullScreen
                  loading='lazy'
                  referrerPolicy='no-referrer-when-downgrade'
                  title='Clinic Location Map'
                />
              </div>
            </div>

            {/* Call to Action Button */}
            <div className='px-6 pb-6'>
              <div
                className='w-full text-white font-semibold text-center py-4 px-6 rounded-lg transition-colors duration-200 '
                style={{ backgroundColor: 'var(--clinic-primary)' }}
              >
                See you soon!
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className='mt-6 max-w-2/3'>
            <button
              onClick={handleChatSupport}
              className='flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-md hover:shadow-lg transition-all duration-200 w-full'
              style={
                {
                  '--hover-border-color': 'var(--clinic-primary)',
                } as React.CSSProperties
              }
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'var(--clinic-primary)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '#E5E7EB';
              }}
            >
              <div className='flex items-center justify-center p-3'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='25'
                  height='25'
                  viewBox='0 0 25 25'
                  fill='none'
                >
                  <path
                    d='M9.17105 9.08081C9.40616 8.41248 9.87021 7.84892 10.481 7.48994C11.0918 7.13097 11.81 6.99975 12.5082 7.11952C13.2065 7.2393 13.8399 7.60233 14.2961 8.14434C14.7524 8.68634 15.0021 9.37233 15.0011 10.0808C15.0011 12.0808 12.0011 13.0808 12.0011 13.0808M12.0811 17.0808H12.0911M22.0811 12.0808C22.0811 17.6037 17.6039 22.0808 12.0811 22.0808C6.55821 22.0808 2.08105 17.6037 2.08105 12.0808C2.08105 6.55796 6.55821 2.08081 12.0811 2.08081C17.6039 2.08081 22.0811 6.55796 22.0811 12.0808Z'
                    stroke='var(--clinic-primary)'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <span className='text-lg font-normal text-gray-500'>
                Do you have any other questions?
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer className='w-full' mobileColumnLayout={true} />
    </div>
  );
}
